// Флаш очереди на сервер.
//
// Синхронизация идёт ТОЛЬКО по инициативе клиента: событие `online`, фоновая проверка,
// ручной retry. Канала «сервер попросил синкнуться» нет — устройство на кухне
// за NAT'ом, и такой канал был бы вторым способом сделать то же самое, только
// ненадёжным.

import { HttpError, NetworkError, httpClient } from '@/shared/api';

import { offlineDb, type QueuedOp } from './db';
import { pendingOps, setSubjectRejected } from './syncQueue';

export type FlushResult = {
  readonly sent: number;
  readonly failed: number;
  /** Осталось в очереди после флаша — для индикатора «в очереди: N». */
  readonly remaining: number;
  /**
   * Сервер отказал субъекту: флаш прерван, очередь цела, нужен вход.
   *
   * Отдельно от `failed` потому, что это разные исходы для человека: «ошибка»
   * означает «эту операцию сервер не принял», а здесь операции исправны и ждут,
   * когда появится тот, от чьего имени их отправлять.
   */
  readonly unauthorized: boolean;
};

/**
 * Идущий прямо сейчас флаш, если он есть.
 *
 * Последовательность ВНУТРИ одного флаша ничего не значила, пока флашей могло идти
 * два (B-008): каждый снимал свой список `pendingOps()` до того, как другой успевал
 * пометить операции `syncing`, и оба отправляли одни и те же. Сервер отвечал на дубль
 * 500, операция уходила в `error` — кухня видела «не отправлено» про этикетку,
 * которая на самом деле создана.
 *
 * Звать флаш из нескольких мест — нормально и нужно: монтирование приложения, кнопка
 * повтора, возврат связи, конец печати. Ненормально было выполнять их одновременно.
 */
let inFlight: Promise<FlushResult> | null = null;

/**
 * Кто-то просил флаш, пока шёл текущий проход (B-025).
 *
 * Проход снимает список операций ОДИН раз, в начале. Всё, что поставлено в очередь
 * после снимка, он не увидит — а подписчик, получивший его промис, свой проход
 * не запускал. Операция оставалась в очереди до постороннего повода.
 *
 * На кухне это выглядело так: массовое списание ставит операции циклом без пауз,
 * первая запускает проход, остальные приходят, пока он в сети, — и до сервера
 * доезжала ОДНА из трёх, при отчёте «Списано: 3». Продукты просили выбросить,
 * а система продолжала считать их активными.
 *
 * Флаг, а не отмена подписки: подписка правильна (второй проход по ТОМУ ЖЕ списку
 * был бы дублем, B-008), недоставало догоняющего прохода по НОВЫМ операциям.
 */
let rerunRequested = false;

/**
 * Потолок ожидания ОДНОГО запроса очереди.
 *
 * У fetch таймаута нет вовсе, и один запрос, повисший на мёртвом соединении,
 * держал `inFlight` вечно: каждый следующий флаш того же человека ПОДПИСЫВАЛСЯ
 * на зависший проход, и очередь замолкала до перезагрузки страницы — без ошибки,
 * без индикатора, с уже показанным «списано» на экране (B-023). На проде так
 * пропали ~10 списаний подряд: nginx их даже не увидел, он логирует только
 * завершённые запросы.
 *
 * Обрыв по таймауту — штатный `NetworkError`: операция возвращается в `pending`,
 * замок снимается, следующий флаш повторит. 30 секунд — с запасом к любой живой
 * кухонной сети и несоизмеримо меньше «навсегда».
 */
/**
 * Подписчики на «очередь что-то доставила до сервера» (B-051).
 *
 * Итог прохода возвращается тому, кто его звал, — и этого мало. Догоняющий проход
 * (`rerunRequested`) запускается БЕЗ вызывающего: его промис никто не ждёт, и всё,
 * что он доставил, остаётся незамеченным экранами. Пакет из пяти операций первый
 * проход отправляет частично, догоняющий — остаток, и список перезапрашивается
 * ровно один раз, в середине доставки: на табло оставалось 4 плитки при 6 активных
 * на сервере, до перезагрузки страницы.
 *
 * Поэтому о доставке сообщает САМ движок, после каждого прохода, а не вызывающий.
 */
const deliveredListeners = new Set<() => void>();

/** Подписка на доставку. Возвращает отписку. */
export function onDelivered(listener: () => void): () => void {
  deliveredListeners.add(listener);
  return () => deliveredListeners.delete(listener);
}

function notifyDelivered(): void {
  for (const listener of deliveredListeners) {
    listener();
  }
}

export const SEND_TIMEOUT_MS = 30_000;

let sendTimeoutMs = SEND_TIMEOUT_MS;

/** Только для тестов: ждать настоящие 30 секунд в тесте нельзя. */
export function setSendTimeoutForTests(ms: number): void {
  sendTimeoutMs = ms;
}

/**
 * Отправить очередь. Возвращает итог для индикатора синхронизации.
 *
 * Идёт СТРОГО последовательно, а не параллельно: порядок операций несущий (`void`
 * не может уйти раньше своего `print`), и параллельная отправка его не сохраняет.
 * Скорость здесь не приоритет — очередь на кухне это единицы операций, а не тысячи.
 *
 * Одновременных проходов не бывает: повторный вызов подписывается на идущий и заказывает
 * догоняющий проход по операциям, появившимся после снимка (B-025).
 *
 * Очередь общая для устройства и уходит под ЛЮБОЙ активной сессией. Автор каждой операции
 * едет в её теле (`op.userId`, зафиксирован в момент тапа), и сервер подписывает действие
 * им — решение Д4 (ревизия 2026-09-08). Раньше проход фильтровался по текущему субъекту,
 * и операции ушедшего повара ждали его возвращения на этом устройстве — на общем
 * моноблоке иногда вечно, и это приходилось показывать менеджеру на экране.
 */
export async function flushQueue(): Promise<FlushResult> {
  if (inFlight !== null) {
    // Подписываемся на идущий проход И заказываем догоняющий: операции, появившиеся
    // после его снимка, иначе никто не отправит (B-025).
    rerunRequested = true;
    return inFlight;
  }

  inFlight = runFlush();
  try {
    return await inFlight;
  } finally {
    inFlight = null;

    if (rerunRequested) {
      rerunRequested = false;
      // Без `await`: вызывающий ждёт итог СВОЕГО прохода, а не всей очереди.
      // Догоняющий проход отправляет остаток и, если за это время попросят ещё,
      // закажет следующий — цепочка сходится, потому что каждая операция после
      // попытки перестаёт быть `pending` (уходит, ошибается или ждёт связи).
      void flushQueue().catch(() => undefined);
    }
  }
}

async function runFlush(): Promise<FlushResult> {
  const ops = await pendingOps();

  let sent = 0;
  let failed = 0;
  let unauthorized = false;

  // Каждый флаш переспрашивает сервер заново: между попытками человек мог войти,
  // и прошлый отказ субъекта не имеет права блокировать очередь навсегда.
  setSubjectRejected(false);

  for (const op of ops) {
    const outcome = await sendOne(op);

    if (outcome === 'sent') {
      sent += 1;
      continue;
    }
    if (outcome === 'failed') {
      failed += 1;
      continue;
    }
    if (outcome === 'unauthorized') {
      // Отказ адресован не операции, а тому, от чьего имени она идёт: следующая
      // получит ровно тот же ответ. Идти дальше значило бы одним протухшим токеном
      // перевести в `error` всю очередь исправных действий — и оставить кухне
      // кнопку повтора, которая гарантированно ничего не изменит.
      unauthorized = true;
      setSubjectRejected(true);
      break;
    }
    // Сети нет — дальше идти бессмысленно, и остальные операции обязаны сохранить
    // свой порядок. Прерываемся, очередь дождётся следующего `online`.
    break;
  }

  if (sent > 0) {
    // ПОСЛЕ прохода, а не после каждой операции: экранам важен факт «серверное
    // состояние изменилось», и дёргать их на каждой записи пакета значило бы
    // устроить N перезапросов там, где достаточно одного.
    notifyDelivered();
  }

  return { sent, failed, unauthorized, remaining: await offlineDb.syncQueue.count() };
}

type Outcome = 'sent' | 'failed' | 'offline' | 'unauthorized';

async function sendOne(op: QueuedOp): Promise<Outcome> {
  await offlineDb.syncQueue.update(op.clientId, { status: 'syncing' });

  try {
    await dispatch(op);
    // Успех — операция удаляется. Повторная доставка той же операции сервером
    // идемпотентна (T-003), но держать её в очереди значило бы отправлять вечно.
    await offlineDb.syncQueue.delete(op.clientId);
    return 'sent';
  } catch (error) {
    if (error instanceof NetworkError) {
      // Не ошибка операции, а отсутствие связи: статус возвращается в `pending`,
      // счётчик попыток НЕ растёт. Иначе неделя без WiFi исчерпала бы лимит
      // на совершенно исправных действиях.
      await offlineDb.syncQueue.update(op.clientId, { status: 'pending' });
      return 'offline';
    }

    if (error instanceof HttpError && (error.status === 401 || error.status === 403)) {
      // Сервер не признал СУБЪЕКТА: сессия истекла, токен отозван, сотрудник
      // заблокирован. Операция при этом исправна — её нельзя считать ошибкой
      // и нельзя тратить на неё попытку, иначе неудачный токен исчерпает лимит
      // на действиях, которые сервер даже не рассматривал.
      //
      // 403 стоит рядом с 401 намеренно: DRF отвечает 401 только когда у схемы
      // аутентификации есть заголовок WWW-Authenticate, и «субъекта нет вовсе»
      // штатно приходит как 403.
      await offlineDb.syncQueue.update(op.clientId, { status: 'pending' });
      return 'unauthorized';
    }

    if (error instanceof HttpError && error.status === 404 && op.type === 'void') {
      // Аннулирование этикетки, которой на сервере НЕТ. Это не ошибка, а достигнутая
      // цель другим путём (B-014).
      //
      // Смысл аннулирования — «эта этикетка не должна существовать в мониторинге».
      // Если сервер о ней не знает вовсе, желаемое состояние уже верно, и отправлять
      // операцию некуда: повторять её можно вечно, ответ не изменится.
      //
      // Откуда берётся такая пара: печать не доехала (например залипла в `syncing`
      // до исправления B-012), а её аннулирование доехало. Локальное схлопывание
      // (T-007) гасит пару, только когда печать ЕЩЁ в очереди; печать, потерянную
      // иначе, схлопывать не с чем.
      //
      // Только для `void`. У `dispose` и `undispose` 404 означает обратное: продукт
      // выброшен физически, а сервер об этом так и не узнает — данные ПОТЕРЯНЫ,
      // и это обязано остаться видимой ошибкой, а не тихо исчезнуть.
      await offlineDb.syncQueue.delete(op.clientId);
      return 'sent';
    }

    // Ошибки синка не проглатываются (code-quality.md §6): статус `error`, текст
    // сохраняется и показывается в интерфейсе с кнопкой повтора.
    await offlineDb.syncQueue.update(op.clientId, {
      status: 'error',
      attempts: op.attempts + 1,
      lastError: describe(error),
    });
    return 'failed';
  }
}

async function dispatch(op: QueuedOp): Promise<void> {
  const JOBS = '/printing/print-jobs';
  // Каждому запросу — свой потолок ожидания. Без него зависший запрос держит
  // `inFlight` вечно и очередь замолкает до перезагрузки (см. SEND_TIMEOUT_MS).
  // Обрыв по сигналу fetch отдаёт как AbortError, httpClient превращает его
  // в NetworkError — то есть ровно в «не доставлено, повторим».
  const send = { signal: AbortSignal.timeout(sendTimeoutMs) };

  switch (op.type) {
    case 'print':
      // `clientId` уезжает как `id` записи — он и есть ключ идемпотентности:
      // флаш → таймаут → ретрай не должен породить вторую этикетку в дашборде.
      // `user` — автор печати, тот, кто ТАПНУЛ: очередь синкается под любой активной
      // сессией, и сервер подписывает запись этим полем (Д4, ревизия 2026-09-08).
      await httpClient.post(`${JOBS}/`, { ...op.payload, id: op.clientId, user: op.userId }, send);
      return;
    case 'void':
      // `user` — автор действия, тот, кто тапнул. Сервер подписывает им, а не
      // субъектом запроса: очередь уходит под любой сессией (Д4, ревизия 2026-09-08).
      await httpClient.post(
        `${JOBS}/${op.clientId}/void/`,
        { acted_at: op.actedAt, user: op.userId },
        send,
      );
      return;
    case 'dispose':
      await httpClient.post(
        `${JOBS}/${op.clientId}/dispose/`,
        { acted_at: op.actedAt, user: op.userId },
        send,
      );
      return;
    case 'undispose':
      await httpClient.post(
        `${JOBS}/${op.clientId}/undispose/`,
        { acted_at: op.actedAt, user: op.userId },
        send,
      );
      return;
    case 'reprint':
      // `clientId` — id НАСЛЕДНИКА (он же ключ идемпотентности на сервере, T-037),
      // источник — в `payload.source_id`. Перепечатка приходит именно отсюда:
      // размокшую этикетку переделывают у полки, где сети может не быть.
      await httpClient.post(
        `${JOBS}/${String(op.payload.source_id)}/reprint/`,
        { id: op.clientId, acted_at: op.actedAt, user: op.userId },
        send,
      );
      return;
    default: {
      const exhaustive: never = op.type;
      throw new Error(`Неизвестный тип операции: ${String(exhaustive)}`);
    }
  }
}

function describe(error: unknown): string {
  if (error instanceof HttpError) {
    const payload = error.payload as { error?: { message?: string } } | undefined;
    return payload?.error?.message ?? `Сервер отклонил операцию (${error.status}).`;
  }
  return 'Не удалось отправить операцию.';
}
